import { useNavigate, useSearchParams } from "react-router-dom";
import { XCircle, ArrowRight, RefreshCw, HelpCircle } from "lucide-react";

export default function PaymentFailurePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const errorCode = searchParams.get("error_code");
  const errorMessage = searchParams.get("error_message");

  const getErrorMessage = () => {
    if (errorMessage) return decodeURIComponent(errorMessage);

    switch (errorCode) {
      case "payment_failed":
        return "The payment could not be processed. Please try again.";
      case "payment_cancelled":
        return "The payment was cancelled. No charges were made.";
      case "verification_failed":
        return "Payment verification failed. Please contact support.";
      default:
        return "Something went wrong with your payment. Please try again.";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Error Icon */}
        <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
          <XCircle className="h-12 w-12 text-red-600" />
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Payment Failed
        </h1>

        {/* Description */}
        <p className="text-gray-600 mb-8">{getErrorMessage()}</p>

        {/* Error Details Card */}
        {errorCode && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8 text-left">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
              Error Details
            </h3>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Error Code</span>
                <span className="font-mono text-sm text-gray-900">
                  {errorCode}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date</span>
                <span className="text-gray-900">
                  {new Date().toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={() => navigate("/subscription")}
            className="w-full inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 transition-colors"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </button>

          <button
            onClick={() => navigate("/dashboard")}
            className="w-full inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
          >
            Go to Dashboard
            <ArrowRight className="ml-2 h-4 w-4" />
          </button>
        </div>

        {/* Help Section */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-start gap-3">
            <HelpCircle className="h-5 w-5 text-gray-400 mt-0.5" />
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">Need help?</p>
              <p className="text-sm text-gray-500 mt-1">
                If you're facing issues with payment, please contact our support
                team at{" "}
                <a
                  href="mailto:support@medicorepay.com"
                  className="text-indigo-600 hover:text-indigo-700"
                >
                  support@medicorepay.com
                </a>{" "}
                or call{" "}
                <a
                  href="tel:+918000000000"
                  className="text-indigo-600 hover:text-indigo-700"
                >
                  +91 80000 00000
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
